import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { initNativeRuntime } from "@/native/runtime";
import { initDeepLinks } from "@/native/deepLinks";

// Send httpOnly auth cookies on every API call. JWT is no longer stored in localStorage —
// the backend sets it as an httpOnly `access_token` cookie at /api/auth/login & /api/auth/register
// and clears it at /api/auth/logout. This blocks XSS token exfiltration.
axios.defaults.withCredentials = true;

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import NativeAuthCallback from "@/pages/NativeAuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import PrivacySettings from "@/pages/PrivacySettings";
import Onboarding from "@/pages/Onboarding";
import Quiz from "@/pages/Quiz";
import Dashboard from "@/pages/Dashboard";
import Discovery from "@/pages/Discovery";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import EntityDetail from "@/pages/EntityDetail";
import WelcomeKit from "@/pages/WelcomeKit";
import Badges from "@/pages/Badges";
import RoutePlanner from "@/pages/RoutePlanner";
import SharedRoute from "@/pages/SharedRoute";
import AdminPanel from "@/pages/AdminPanel";

// Components
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/ThemeProvider";
import BottomTabNav from "@/components/layout/BottomTabNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // JWT lives in an httpOnly cookie now (not localStorage). We keep the
  // in-memory token only for legacy Authorization-Bearer call sites still
  // present in some pages — backend prefers the cookie regardless.
  const [token, setToken] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // Cookie is sent automatically via withCredentials. Bearer header is
      // included only when we have an in-memory token (post-login same session).
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async (credential) => {
    // credential = Google-issued ID token from @react-oauth/google
    const response = await axios.post(`${API}/auth/google`, { credential });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setToken(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const setAuthToken = (newToken) => {
    setToken(newToken);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      token,
      login, 
      register, 
      loginWithGoogle,
      logout, 
      updateUser,
      setAuthToken,
      checkAuth,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Native deep-link bridge: when the user taps unityblaze://r/<token> or a
  // verified Universal Link, navigate via React Router so they land directly
  // on the SharedRoute page (auth-aware). Web is a no-op (browser already
  // owns the URL bar).
  useEffect(() => {
    let cleanup = () => {};
    initDeepLinks(navigate).then((fn) => {
      cleanup = fn || (() => {});
    });
    return () => cleanup();
  }, [navigate]);

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hide bottom-tab nav on landing, auth, onboarding, quiz, and admin (admin has its own UX)
  const HIDE_BOTTOM_NAV_PATHS = ['/', '/login', '/register', '/onboarding', '/quiz'];
  const showBottomNav = !!user && !HIDE_BOTTOM_NAV_PATHS.includes(location.pathname) && !location.pathname.startsWith('/admin');

  return (
    <>
      {/* Reserve space for bottom nav on mobile when shown */}
      <div className={showBottomNav ? 'pb-[72px] md:pb-0' : ''}>
        <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/native-callback" element={<NativeAuthCallback />} />
      <Route path="/r/:token" element={<SharedRoute />} />
      
      {/* Protected Routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/quiz" element={
        <ProtectedRoute>
          <Quiz />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/discovery" element={
        <ProtectedRoute>
          <Discovery />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/settings/privacy" element={
        <ProtectedRoute>
          <PrivacySettings />
        </ProtectedRoute>
      } />
      <Route path="/entity/:entityId" element={<EntityDetail />} />
      <Route path="/welcome-kit" element={
        <ProtectedRoute>
          <WelcomeKit />
        </ProtectedRoute>
      } />
      <Route path="/badges" element={
        <ProtectedRoute>
          <Badges />
        </ProtectedRoute>
      } />
      <Route path="/route-planner" element={
        <ProtectedRoute>
          <RoutePlanner />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </div>
      {showBottomNav && <BottomTabNav />}
    </>
  );
}

function App() {
  useEffect(() => {
    // No-op on web; on iOS/Android Capacitor wrappers this hides the splash
    // screen, applies status-bar style, and wires the hardware back button.
    initNativeRuntime({
      onAppResume: () => {
        // App returned from background — could refetch /auth/me here later
        // if we want to handle long sessions / cookie expiry gracefully.
      },
    });
  }, []);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="unity-blaze-theme">
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </AuthProvider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}

export default App;
export { API, BACKEND_URL };
